import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";
import zhCN from "./locales/zh-CN.json";

/** UI languages the web app ships translations for. Mirrors the server's
 * `SUPPORTED_UI_LANGUAGES` allowlist in routes/me.ts. */
export const SUPPORTED_LANGUAGES = ["en", "zh-CN"] as const;
export type UiLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Collapse any stored/browser/account tag down to exactly one supported
 * locale. Any `zh*` variant (zh, zh-CN, zh-Hans, zh-TW, …) maps to our single
 * Simplified bundle; everything else falls back to English. This keeps us from
 * ever activating a tag we have no resources for.
 */
export function normalizeLanguage(tag: string | null | undefined): UiLanguage {
  if (!tag) return "en";
  return tag.toLowerCase().startsWith("zh") ? "zh-CN" : "en";
}

/** Key under which the detector caches the chosen language in localStorage. */
export const LANG_STORAGE_KEY = "tp-lang";

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      "zh-CN": { translation: zhCN },
    },
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // We only ship the exact tags above; never try to load a base "zh".
    load: "currentOnly",
    nonExplicitSupportedLngs: false,
    detection: {
      // Account preference is applied separately via changeLanguage() after
      // /api/me resolves; on first paint we rely on the cache then the browser.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
      convertDetectedLanguage: (lng) => normalizeLanguage(lng),
    },
    interpolation: { escapeValue: false }, // React already escapes.
    returnNull: false,
  });

// Keep <html lang> in sync for accessibility and SEO.
const applyHtmlLang = (lng: string) => {
  document.documentElement.lang = normalizeLanguage(lng);
};
applyHtmlLang(i18n.language);
i18n.on("languageChanged", applyHtmlLang);

export default i18n;
